import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, Output } from '@angular/core';
import { TranslateModule } from '@ngx-translate/core';
import { TaskProgressColorMap } from '@seahorse/domain';
import { ShButtonComponent, ShNumberComponent } from '@seahorse/components';
import { ShCellDirective, ShTableComponent } from '@seahorse/shell';
import type { ShColumn } from '@seahorse/shell';
import type { ActionMenuItem } from '@seahorse/ui';

/**
 * The colour per progress boundary of the task progress display: a boundary
 * (0 to 100) and its colour per row. The colour stays a native colour input:
 * no sh-* control picks a colour. Edits the rows in place; adding and
 * removing emit a new list (`colorMapChange`).
 */
@Component({
  selector: 'su-task-progress-color-map-editor',
  imports: [
    CommonModule,
    TranslateModule,
    ShButtonComponent,
    ShNumberComponent,
    ShTableComponent,
    ShCellDirective,
  ],
  template: `
    <sh-table
      tableId="ui-module-task-progress-colors"
      density="compact"
      [columns]="columns"
      [rows]="colorMap"
      [rowActions]="rowActions"
    >
      <ng-template shCell="boundary" let-row>
        <sh-number
          ariaLabel="uiModule.taskProgress.colorMap.boundary"
          [min]="0"
          [max]="100"
          [(value)]="row.boundary"
        ></sh-number>
      </ng-template>
      <ng-template shCell="color" let-row>
        <input
          type="color"
          class="color-input"
          [value]="row.color"
          (input)="setColor(row, $any($event.target).value)"
          [attr.aria-label]="'uiModule.taskProgress.colorMap.color' | translate"
        />
      </ng-template>
    </sh-table>
    <div class="mt-2">
      <sh-button
        label="shared.terms.add"
        severity="secondary"
        size="sm"
        icon="fa-regular fa-plus"
        (click)="addColorMap()"
      ></sh-button>
    </div>
  `,
})
export class TaskProgressColorMapEditorComponent {
  @Input() colorMap: TaskProgressColorMap[] = [];
  @Output() colorMapChange = new EventEmitter<TaskProgressColorMap[]>();

  // Stable fields: they feed sh-table.
  readonly columns: ShColumn<TaskProgressColorMap>[] = [
    {
      key: 'boundary',
      header: 'uiModule.taskProgress.colorMap.boundary',
      type: 'custom',
    },
    {
      key: 'color',
      header: 'uiModule.taskProgress.colorMap.color',
      type: 'custom',
    },
  ];

  readonly rowActions: ActionMenuItem<TaskProgressColorMap>[] = [
    {
      label: 'shared.terms.remove',
      icon: 'fa-regular fa-trash',
      danger: true,
      command: (row) => this.removeColorMap(row),
    },
  ];

  addColorMap() {
    this.colorMap = [...this.colorMap, { boundary: 0, color: '#ffffff' }];
    this.colorMapChange.emit(this.colorMap);
  }

  removeColorMap(entry: TaskProgressColorMap) {
    this.colorMap = this.colorMap.filter((x) => x !== entry);
    this.colorMapChange.emit(this.colorMap);
  }

  setColor(entry: TaskProgressColorMap, color: string) {
    entry.color = color;
  }
}
